const LOGO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/an7aTMn8v3udhR5ryjHUfw/dance_for_fans_logo.png'

const BRAND = 'Dance for Fans'
const SITE_URL = 'https://www.danceforfans.com'

export interface PageMeta {
  /** Short, page-specific title fragment (e.g. "Investors", "The Origin Story"). Pass empty string for the home page. */
  title?: string
  /** Page description for OG / Twitter / meta description. Will be prepended with the brand name if not already included. */
  description: string
  /** Optional override image URL. Defaults to the Dance for Fans neon logo. */
  image?: string
}

/**
 * Sets a fully branded set of meta tags for share-link previews.
 * Always includes "Dance for Fans" in the title and description so that
 * any shared URL renders consistent branding on Twitter, Facebook, iMessage, etc.
 */
export function setPageMeta({ title, description, image }: PageMeta) {
  // Compose a branded title. "Dance for Fans" always appears.
  const fullTitle = title
    ? `${title} · ${BRAND} · danceforfans.com`
    : `${BRAND} · danceforfans.com`

  // Ensure description references the brand at the start for consistency.
  const brandedDescription = description
    .toLowerCase()
    .includes('dance for fans')
    ? description
    : `${BRAND} — ${description}`

  const finalImage = image || LOGO_IMAGE

  document.title = fullTitle

  const setMeta = (
    selector: string,
    attr: 'name' | 'property',
    key: string,
    content: string,
  ) => {
    let el = document.head.querySelector<HTMLMetaElement>(selector)
    if (!el) {
      el = document.createElement('meta')
      el.setAttribute(attr, key)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }

  // Standard
  setMeta('meta[name="description"]', 'name', 'description', brandedDescription)

  // Open Graph
  setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', BRAND)
  setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle)
  setMeta(
    'meta[property="og:description"]',
    'property',
    'og:description',
    brandedDescription,
  )
  setMeta('meta[property="og:type"]', 'property', 'og:type', 'website')
  setMeta('meta[property="og:url"]', 'property', 'og:url', SITE_URL)
  setMeta('meta[property="og:image"]', 'property', 'og:image', finalImage)
  setMeta(
    'meta[property="og:image:alt"]',
    'property',
    'og:image:alt',
    `${BRAND} — danceforfans.com`,
  )

  // Twitter
  setMeta(
    'meta[name="twitter:card"]',
    'name',
    'twitter:card',
    'summary_large_image',
  )
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle)
  setMeta(
    'meta[name="twitter:description"]',
    'name',
    'twitter:description',
    brandedDescription,
  )
  setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', finalImage)
  setMeta(
    'meta[name="twitter:image:alt"]',
    'name',
    'twitter:image:alt',
    `${BRAND} — danceforfans.com`,
  )
}
